import React, { useEffect, useRef, useState, useCallback } from 'react';
import useToken from '../../hooks/useToken';
import DocIntroDialog from '../shared/DocIntroDialog';
import TmViewerTourJoyride from '../shared/TmViewerTourJoyride';
import DRQuestionPanel from '../shared/DRQuestionPanel';

const LOG_ENABLED = true;

const SEVERITY_ORDER = { INFO: 0, LOW: 1, MEDIUM: 2, MED: 2, HIGH: 3, CRITICAL: 4 };
const STRIDE_ORDER   = { S: 0, T: 1, R: 2, I: 3, D: 4, E: 5 };
const SEVERITY_COLOR = {
  CRITICAL: { bg: '#fde8e8', text: '#b00020' },
  HIGH:     { bg: '#fff3e0', text: '#e65100' },
  MEDIUM:   { bg: '#fffde7', text: '#f57f17' },
  MED:      { bg: '#fffde7', text: '#f57f17' },
  LOW:      { bg: '#e8f5e9', text: '#2e7d32' },
  INFO:     { bg: '#e3f2fd', text: '#1565c0' },
};

const STRIDE_COLOR = {
  S: '#7c3aed',
  T: '#ea580c',
  R: '#0891b2',
  I: '#2563eb',
  D: '#dc2626',
  E: '#16a34a',
};
const STRIDE_LABEL = {
  S: 'Spoofing',
  T: 'Tampering',
  R: 'Repudiation',
  I: 'Information Disclosure',
  D: 'Denial of Service',
  E: 'Elevation of Privilege',
};

/* ── Full columns (bottom panel — commented out, kept for reference) ──
const COLUMNS = [
  { key: 'id',                    label: 'ID',             width: 80 },
  { key: 'statement',             label: 'Statement',      width: 340 },
  { key: 'stride',                label: 'STRIDE',         width: 70 },
  { key: 'attacker',              label: 'Attacker',       width: 80 },
  { key: 'threat_agent_avg',      label: 'Agent Avg',      width: 85 },
  { key: 'vulnerability_avg',     label: 'Vuln Avg',       width: 80 },
  { key: 'likelihood_avg',        label: 'Likelihood Avg', width: 115 },
  { key: 'likelihood_level',      label: 'Likelihood',     width: 95 },
  { key: 'technical_impact_avg',  label: 'Tech Impact Avg',width: 120 },
  { key: 'technical_impact_level',label: 'Tech Impact',    width: 100 },
  { key: 'business_impact_avg',   label: 'Biz Impact Avg', width: 115 },
  { key: 'business_impact_level', label: 'Biz Impact',     width: 95 },
  { key: 'severity',              label: 'Severity',       width: 90 },
  { key: 'risk_score',            label: 'Risk Score',     width: 90 },
];
const PANEL_MIN_H    = 120;
const PANEL_MAX_H    = 600;
const PANEL_DEFAULT_H = 280;
── end bottom panel ── */

const SIDEBAR_DEFAULT_WIDTH = 340;
const SIDEBAR_MIN_WIDTH     = 220;
const SORT_OPTIONS = [
  { key: 'stride',               label: 'STRIDE'      },
  { key: 'severity',             label: 'Severity'    },
  { key: 'risk_score',           label: 'Risk'        },
  { key: 'likelihood_avg',       label: 'Likelihood'  },
  { key: 'technical_impact_avg', label: 'Tech Impact' },
  { key: 'business_impact_avg',  label: 'Biz Impact'  },
];

// ── API path row ─────────────────────────────────────────────────────────────
function ApiPathRow({ path, scale = 1 }) {
  const s = scale;
  return (
    <div style={{
      fontFamily: 'monospace',
      fontSize: Math.round(12.5 * s * 10) / 10,
      background: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: Math.round(6 * s),
      padding: `${Math.round(7 * s)}px ${Math.round(11 * s)}px`,
      marginBottom: Math.round(6 * s),
      color: '#1a237e',
      wordBreak: 'break-all',
      boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    }}>
      {path}
    </div>
  );
}

// ── Threat card ──────────────────────────────────────────────────────────────
function ThreatCard({ threat, scale = 1, isSelected = false, onSelect, id }) {
  const s           = scale;
  const stride      = threat.stride || '?';
  const strideColor = STRIDE_COLOR[stride] || '#888';
  const sc          = SEVERITY_COLOR[threat.severity] || {};

  return (
    <div
      id={id}
      onClick={onSelect}
      style={{
        borderLeft: `4px solid ${strideColor}`,
        borderRadius: `0 ${Math.round(8*s)}px ${Math.round(8*s)}px 0`,
        background: isSelected ? '#f3e5f5' : '#fff',
        boxShadow: isSelected
          ? '0 0 0 2.5px #5e35b1, 0 2px 6px rgba(94,53,177,0.18)'
          : '0 1px 4px rgba(0,0,0,0.09)',
        marginBottom: Math.round(8*s),
        padding: `${Math.round(9*s)}px ${Math.round(11*s)}px`,
        cursor: onSelect ? 'pointer' : 'default',
        transition: 'background 0.15s, box-shadow 0.15s',
      }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: Math.round(5*s) }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: Math.round(7*s), minWidth: 0 }}>
          <span
            title={STRIDE_LABEL[stride] || stride}
            style={{
              backgroundColor: strideColor,
              color: '#fff',
              padding: `${Math.round(1*s)}px ${Math.round(8*s)}px`,
              borderRadius: Math.round(10*s),
              fontSize: Math.round(10.5*s*10)/10,
              fontWeight: 700,
              letterSpacing: '0.04em',
              cursor: 'default',
              flexShrink: 0,
            }}
          >
            {stride}
          </span>
          {threat.title && (
            <span style={{
              fontSize: Math.round(11.5*s*10)/10,
              fontWeight: 600,
              color: '#333',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {threat.title}
            </span>
          )}
        </div>
        <span style={{ fontSize: Math.round(10.5*s*10)/10, color: '#bbb', fontFamily: 'monospace', fontWeight: 600, flexShrink: 0 }}>#{threat.id}</span>
      </div>

      <p style={{ margin: `0 0 ${Math.round(8*s)}px 0`, fontSize: Math.round(12.5*s*10)/10, lineHeight: 1.45, color: '#222' }}>
        {threat.statement}
      </p>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: Math.round(4*s) }}>
        <span style={{
          backgroundColor: sc.bg || '#eee',
          color: sc.text || '#333',
          padding: `${Math.round(2*s)}px ${Math.round(9*s)}px`,
          borderRadius: Math.round(10*s),
          fontSize: Math.round(10.5*s*10)/10,
          fontWeight: 700,
        }}>
          {threat.severity ?? '—'}
        </span>
        <div style={{ display: 'flex', gap: Math.round(7*s), fontSize: Math.round(11*s*10)/10, color: '#555' }}>
          <span title="Likelihood avg"   style={metricStyle}>⚡ {fmt(threat.likelihood_avg)}</span>
          <span title="Tech Impact avg"  style={metricStyle}>⚙ {fmt(threat.technical_impact_avg)}</span>
          <span title="Biz Impact avg"   style={metricStyle}>📈 {fmt(threat.business_impact_avg)}</span>
          <span title="Risk Score"       style={{ ...metricStyle, fontWeight: 700, color: '#333' }}>⚠ {fmt(threat.risk_score)}</span>
        </div>
      </div>
    </div>
  );
}

const fmt    = v => (v == null ? '—' : typeof v === 'number' ? v.toFixed(1) : v);
const metricStyle = { whiteSpace: 'nowrap' };

// ── Main component ────────────────────────────────────────────────────────────
export default function ThreatModelViewerPage() {
  const { token, refreshAccessToken, getValidToken } = useToken();
  const [svgContent, setSvgContent]     = useState('');
  const [loading, setLoading]           = useState(true);
  const [msg, setMsg]                   = useState('');
  const loadedRef                       = useRef(false);

  const [threatsMap, setThreatsMap]         = useState({});
  const [labelsMap, setLabelsMap]           = useState({});
  const [apiPathsMap, setApiPathsMap]       = useState({});
  const [selected, setSelected]             = useState(null);
  const [loadingThreats, setLoadingThreats] = useState(true);
  const [threatsError, setThreatsError]     = useState(null);

  const [zoom, setZoom]             = useState(1);
  const [pan, setPan]               = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning]   = useState(false);
  const [textScale, setTextScale]   = useState(() => {
    const s = parseFloat(localStorage.getItem('dfd_text_scale'));
    return isNaN(s) ? 1 : s;
  });
  const [sidebarOpen, setSidebarOpen]       = useState(true);
  const [sidebarWidth, setSidebarWidth]     = useState(SIDEBAR_DEFAULT_WIDTH);
  const sidebarResizing    = useRef(false);
  const sidebarResizeStartX = useRef(0);
  const sidebarResizeStartW = useRef(0);
  const [sidebarTab, setSidebarTab]         = useState('threats'); // 'threats' | 'paths'
  const [selectedThreatId, setSelectedThreatId] = useState(null);
  const highlightCompRef = useRef(null);
  const [sortKey, setSortKey]       = useState('stride');
  const [sortDir, setSortDir]       = useState('asc');
  const [threatModelProvided, setThreatModelProvided] = useState(false);
  const [introOpen, setIntroOpen]   = useState(false);
  const [joyrideRun, setJoyrideRun]     = useState(false);
  const [tourKey, setTourKey]           = useState(0);

  /* Bottom panel state (commented out)
  const [panelHeight, setPanelHeight] = useState(PANEL_DEFAULT_H);
  const dragRef = useRef(null);
  */

  const tourContext = new URLSearchParams(window.location.search).get('quiz') === '1' ? 'doc_review' : 'experiment';

  const authRef    = useRef(null);
  const liveRef    = useRef({ zoom: 1, panX: 0, panY: 0 });
  const isDragging = useRef(false);
  const dragStart  = useRef({ x: 0, y: 0, panX: 0, panY: 0 });
  useEffect(() => { authRef.current = token; }, [token]);

  const applyTransform = useCallback((newZoom, newPan) => {
    const clamped = Math.min(3, Math.max(0.05, newZoom));
    liveRef.current = { zoom: clamped, panX: newPan.x, panY: newPan.y };
    setZoom(clamped);
    setPan(newPan);
  }, []);

  const fitToCanvas = useCallback(() => {
    const viewport = document.getElementById('dfdViewport');
    if (!viewport) return;
    const svg = viewport.querySelector('svg');
    if (!svg) return;
    let svgW, svgH;
    const vb = svg.getAttribute('viewBox');
    if (vb) {
      const parts = vb.trim().split(/[\s,]+/);
      svgW = parseFloat(parts[2]);
      svgH = parseFloat(parts[3]);
    } else {
      svgW = parseFloat(svg.getAttribute('width')) || 800;
      svgH = parseFloat(svg.getAttribute('height')) || 600;
    }
    const viewW = viewport.clientWidth;
    const viewH = viewport.clientHeight;
    const fitZoom = Math.min((viewW - 40) / svgW, (viewH - 40) / svgH);
    applyTransform(fitZoom, { x: (viewW - svgW * fitZoom) / 2, y: (viewH - svgH * fitZoom) / 2 });
  }, [applyTransform]);

  const logEvent = useCallback(async (type, details = {}) => {
    if (!LOG_ENABLED) return;
    let auth = authRef.current || (await getValidToken().catch(() => null));
    if (!auth) return;
    fetch('/api/threat-model/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth}` },
      body: JSON.stringify({ event: type, timestamp: new Date().toISOString(), ...details }),
    }).catch(() => {});
  }, [getValidToken]);

  const markTourSeen = useCallback(async websiteId => {
    const auth = authRef.current || (await getValidToken().catch(() => null));
    if (!auth) return;
    fetch(`/api/website/${websiteId}/threat-model/tour-seen`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${auth}` },
      body: JSON.stringify({ context: tourContext }),
    }).catch(() => {});
  }, [getValidToken, tourContext]);

  // Initial data load
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    (async () => {
      try {
        const authToken = await getValidToken();
        const assignRes = await fetch('/api/assignment', { headers: { Authorization: `Bearer ${authToken}` } });
        if (!assignRes.ok) throw new Error('Failed to load assignments');
        const assignments = await assignRes.json();
        const assignedWebsite = assignments.find(w => w.order_index === 1);
        if (!assignedWebsite) { setMsg('You are not assigned to Website #1.'); setLoading(false); return; }
        setThreatModelProvided(!!assignedWebsite.threat_model_provided);

        const dfdRes = await fetch(`/api/website/${assignedWebsite.website_id}/threat-model`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!dfdRes.ok) { setMsg('❌ The Threat Model is not available for this website.'); setLoading(false); return; }
        const data = await dfdRes.json();
        if (!data?.svg) { setMsg('❌ Empty SVG payload.'); setLoading(false); return; }
        setSvgContent(data.svg);
        setLoading(false);

        const thrRes = await fetch(`/api/website/${assignedWebsite.website_id}/threat-list`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!thrRes.ok) throw new Error(`Threats HTTP ${thrRes.status}`);
        const rawThreats = await thrRes.json();
        const { component_labels: labels = {}, ...threatsOnly } = rawThreats;
        setLabelsMap(labels);
        setThreatsMap(threatsOnly);

        // api-paths: silently skip if unavailable (e.g. website 2 has no file yet)
        try {
          const pathsRes = await fetch(`/api/website/${assignedWebsite.website_id}/api-paths`, {
            headers: { Authorization: `Bearer ${authToken}` },
          });
          if (pathsRes.ok) setApiPathsMap(await pathsRes.json());
        } catch (_) {}

        setLoadingThreats(false);
        logEvent('open', { page: 'DFD_VIEWER_PAGE1', website_id: assignedWebsite.website_id });
        const tourSeenAt = tourContext === 'doc_review'
          ? assignedWebsite.dfd_tour_seen_doc_review_at
          : assignedWebsite.dfd_tour_seen_experiment_at;
        if (!tourSeenAt) {
          markTourSeen(assignedWebsite.website_id);
          requestAnimationFrame(() => { fitToCanvas(); setJoyrideRun(true); });
        }
      } catch (err) {
        console.error(err);
        setMsg('Failed to load the Threat Model.');
        setThreatsError('Failed to load threats.');
        setLoading(false);
        setLoadingThreats(false);
      }
    })();
  }, [token, refreshAccessToken, logEvent]);

  // Auto-fit diagram to canvas when SVG first loads
  useEffect(() => {
    if (!svgContent) return;
    requestAnimationFrame(() => fitToCanvas());
  }, [svgContent, fitToCanvas]);

  // SVG click / hover instrumentation
  useEffect(() => {
    if (!svgContent) return;
    const container = document.getElementById('dfdContainer');
    if (!container) return;

    const seen = new Set();
    container.querySelectorAll('title').forEach(titleEl => {
      const id = titleEl.textContent.trim();
      if (!id || seen.has(id)) return;
      let node = titleEl.parentElement;
      while (node && node !== container) {
        if (node.tagName?.toLowerCase() === 'g' && node.getAttribute('data-cell-id')) {
          if (!node.hasAttribute('data-semantic-id')) {
            node.setAttribute('data-semantic-id', id);
            if (id.endsWith('_df')) node.setAttribute('data-element-type', 'dataflow');
          }
          // Don't add 'fake' to seen — allow all fake elements to be stamped
          if (id !== 'fake') seen.add(id);
          break;
        }
        node = node.parentElement;
      }
    });

    // Inject invisible wide hit-area overlays on dataflow paths so thin lines are easy to hover/click
    container.querySelectorAll('[data-element-type="dataflow"]').forEach(groupEl => {
      groupEl.querySelectorAll('path, line, polyline').forEach(shapeEl => {
        if (shapeEl.getAttribute('data-hit-area')) return;
        const hit = shapeEl.cloneNode(false);
        hit.setAttribute('data-hit-area', 'true');
        hit.removeAttribute('marker-end');
        hit.removeAttribute('marker-start');
        // Inline !important keeps CSS hover/selected rules from making the overlay visible
        hit.style.cssText = 'stroke: transparent !important; fill: none !important; stroke-width: 20px !important; cursor: pointer !important;';
        groupEl.appendChild(hit);
      });
    });

    const getSemanticEl = target => {
      let node = target;
      while (node && node !== container) {
        const id = node.getAttribute?.('data-semantic-id');
        if (id) return (id === 'fake' || id === 'semi-transparent') ? null : { el: node, id };
        node = node.parentElement;
      }
      return null;
    };

    const getDataflowEndpoints = id => {
      if (!id.endsWith('_df')) return [];
      return id.slice(0, -3).split('-').map(part =>
        container.querySelector(`[data-semantic-id="${part}"]`)
      ).filter(Boolean);
    };

    // Exposed to threat-card clicks so they can highlight the DFD without changing selected state
    highlightCompRef.current = compId => {
      container.querySelectorAll('[data-semantic-id].selected').forEach(el => {
        el.classList.remove('selected');
        getDataflowEndpoints(el.getAttribute('data-semantic-id')).forEach(r => r.classList.remove('selected-related'));
      });
      if (!compId) return;
      const el = container.querySelector(`[data-semantic-id="${compId}"]`);
      if (!el) return;
      el.classList.add('selected');
      getDataflowEndpoints(compId).forEach(r => r.classList.add('selected-related'));
    };

    const clearFocus = () => {
      container.querySelectorAll('[data-semantic-id].focused').forEach(el => {
        el.classList.remove('focused');
        getDataflowEndpoints(el.getAttribute('data-semantic-id')).forEach(r => r.classList.remove('focused-related'));
      });
      container.classList.remove('has-focus');
    };

    const onHover = e => {
      const f = getSemanticEl(e.target);
      if (!f) return;
      logEvent('hover', { element: f.id });
      f.el.classList.add('hovered');
      getDataflowEndpoints(f.id).forEach(el => el.classList.add('hovered-related'));
    };
    const onLeave = e => {
      const f = getSemanticEl(e.target);
      if (!f) return;
      f.el.classList.remove('hovered');
      getDataflowEndpoints(f.id).forEach(el => el.classList.remove('hovered-related'));
    };
    const onClick = e => {
      const f = getSemanticEl(e.target);
      if (!f) {
        if (container.classList.contains('has-focus')) { clearFocus(); logEvent('focus_clear', { trigger: 'background_click' }); }
        container.querySelectorAll('[data-semantic-id].selected').forEach(el => {
          el.classList.remove('selected');
          getDataflowEndpoints(el.getAttribute('data-semantic-id')).forEach(r => r.classList.remove('selected-related'));
        });
        setSelected(prev => { if (prev) logEvent('deselect', { component: prev }); return null; });
        setSelectedThreatId(null);
        return;
      }
      logEvent('click', { element: f.id });
      // Deselect previous element and its related endpoints
      container.querySelectorAll('[data-semantic-id].selected').forEach(el => {
        el.classList.remove('selected');
        getDataflowEndpoints(el.getAttribute('data-semantic-id')).forEach(r => r.classList.remove('selected-related'));
      });
      f.el.classList.add('selected');
      getDataflowEndpoints(f.id).forEach(el => el.classList.add('selected-related'));
      setSelected(prev => {
        if (prev) logEvent('close_threats', { component: prev });
        logEvent('open_threats', { component: f.id });
        return f.id;
      });
      setSelectedThreatId(null);
      setSidebarTab('threats');
      setSidebarOpen(true);
    };
    const onDblClick = e => {
      const f = getSemanticEl(e.target);
      if (!f) return;
      // Toggle off if same element is already focused
      if (container.classList.contains('has-focus') && f.el.classList.contains('focused')) {
        clearFocus();
        logEvent('focus_clear', { element: f.id, trigger: 'dblclick_toggle' });
        return;
      }
      clearFocus();
      f.el.classList.add('focused');
      getDataflowEndpoints(f.id).forEach(el => el.classList.add('focused-related'));
      container.classList.add('has-focus');
      logEvent('focus', { element: f.id });
    };

    container.addEventListener('mouseover',  onHover);
    container.addEventListener('mouseout',   onLeave);
    container.addEventListener('click',      onClick);
    container.addEventListener('dblclick',   onDblClick);
    return () => {
      container.removeEventListener('mouseover',  onHover);
      container.removeEventListener('mouseout',   onLeave);
      container.removeEventListener('click',      onClick);
      container.removeEventListener('dblclick',   onDblClick);
    };
  }, [svgContent, logEvent]);

  // Non-passive wheel listener: zoom toward cursor (Ctrl+wheel or plain wheel)
  useEffect(() => {
    if (!svgContent) return;
    const viewport = document.getElementById('dfdViewport');
    if (!viewport) return;
    const onWheel = e => {
      e.preventDefault();
      const { zoom: curZoom, panX, panY } = liveRef.current;
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      const newZoom = Math.min(3, Math.max(0.05, curZoom * factor));
      const rect = viewport.getBoundingClientRect();
      const mx = e.clientX - rect.left;
      const my = e.clientY - rect.top;
      applyTransform(newZoom, {
        x: mx - (mx - panX) * (newZoom / curZoom),
        y: my - (my - panY) * (newZoom / curZoom),
      });
    };
    viewport.addEventListener('wheel', onWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', onWheel);
  }, [svgContent, applyTransform]);

  // Visibility logging
  useEffect(() => {
    const onVis = () => logEvent('visibility_change', { visible: !document.hidden });
    document.addEventListener('visibilitychange', onVis);
    return () => document.removeEventListener('visibilitychange', onVis);
  }, [logEvent]);

  // Sidebar drag-resize
  useEffect(() => {
    const onMouseMove = e => {
      if (!sidebarResizing.current) return;
      const delta = sidebarResizeStartX.current - e.clientX;
      const maxW  = Math.floor(window.innerWidth / 2);
      setSidebarWidth(Math.min(maxW, Math.max(SIDEBAR_MIN_WIDTH, sidebarResizeStartW.current + delta)));
    };
    const onMouseUp = () => {
      if (!sidebarResizing.current) return;
      sidebarResizing.current = false;
      document.body.style.cursor     = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup',   onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup',   onMouseUp);
    };
  }, []);

  /* Bottom panel drag-resize (commented out)
  useEffect(() => {
    const onMouseMove = e => {
      if (!dragRef.current) return;
      const delta = dragRef.current.startY - e.clientY;
      const next = Math.min(PANEL_MAX_H, Math.max(PANEL_MIN_H, dragRef.current.startH + delta));
      setPanelHeight(next);
    };
    const onMouseUp = () => { dragRef.current = null; };
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup',   onMouseUp);
    return () => { window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp); };
  }, []);
  */

  const handleSort = key => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortedThreats = () => {
    const list = selected
      ? (threatsMap[selected] ? threatsMap[selected].map(t => ({ ...t, _component: selected })) : [])
      : Object.entries(threatsMap).flatMap(([comp, ts]) => ts.map(t => ({ ...t, _component: comp })));
    return list.sort((a, b) => {
      let av = a[sortKey], bv = b[sortKey];
      if (sortKey === 'severity') { av = SEVERITY_ORDER[av] ?? 99; bv = SEVERITY_ORDER[bv] ?? 99; }
      if (sortKey === 'stride')   { av = STRIDE_ORDER[av]   ?? 99; bv = STRIDE_ORDER[bv]   ?? 99; }
      if (av == null) return 1;
      if (bv == null) return -1;
      if (av < bv) return sortDir === 'asc' ? -1 : 1;
      if (av > bv) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const handleSliderChange = val => {
    const viewport = document.getElementById('dfdViewport');
    const newZoom = val / 100;
    if (!viewport) { applyTransform(newZoom, { x: liveRef.current.panX, y: liveRef.current.panY }); return; }
    const { zoom: curZoom, panX, panY } = liveRef.current;
    const cx = viewport.clientWidth / 2;
    const cy = viewport.clientHeight / 2;
    applyTransform(newZoom, {
      x: cx - (cx - panX) * (newZoom / curZoom),
      y: cy - (cy - panY) * (newZoom / curZoom),
    });
  };

  const adjustTextScale = delta => {
    setTextScale(prev => {
      const next = Math.min(1.8, Math.max(0.7, Math.round((prev + delta) * 10) / 10));
      localStorage.setItem('dfd_text_scale', next);
      return next;
    });
  };

  const isDataflow      = selected?.endsWith('_df') ?? false;
  const threats         = sortedThreats();
  const apiPaths        = selected ? (apiPathsMap[selected] ?? []) : Object.values(apiPathsMap).flat();
  const sidebarCount    = sidebarTab === 'paths' ? apiPaths.length : threats.length;
  const widthScale      = Math.max(0.8, Math.min(1.4, sidebarWidth / SIDEBAR_DEFAULT_WIDTH));
  const adaptiveScale   = Math.max(0.7, Math.min(1.8, textScale * widthScale));

  return (
    <div id="tm-viewer-root" style={{ display: 'flex', flexDirection: 'column', height: '100%', fontFamily: 'Arial, sans-serif', overflow: 'hidden' }}>

      {/* Global SVG highlight styles */}
      <style>{`
        #dfdContainer [data-semantic-id] rect,
        #dfdContainer [data-semantic-id] path,
        #dfdContainer [data-semantic-id] ellipse,
        #dfdContainer [data-semantic-id] circle,
        #dfdContainer [data-semantic-id] line,
        #dfdContainer [data-semantic-id] polyline { transition: fill 0.15s ease, stroke 0.15s ease; }

        /* Component hover: blue fill + stroke */
        #dfdContainer [data-semantic-id]:not([data-element-type="dataflow"]).hovered rect,
        #dfdContainer [data-semantic-id]:not([data-element-type="dataflow"]).hovered path,
        #dfdContainer [data-semantic-id]:not([data-element-type="dataflow"]).hovered ellipse,
        #dfdContainer [data-semantic-id]:not([data-element-type="dataflow"]).hovered circle { stroke: #1976d2 !important; fill: #bbdefb !important; cursor: pointer; }

        /* Dataflow hover: blue stroke, thicker line */
        #dfdContainer [data-element-type="dataflow"].hovered path,
        #dfdContainer [data-element-type="dataflow"].hovered line,
        #dfdContainer [data-element-type="dataflow"].hovered polyline { stroke: #1976d2 !important; stroke-width: 4px !important; cursor: pointer; }

        /* Hovered-related: endpoints lit when a dataflow is hovered */
        #dfdContainer [data-semantic-id].hovered-related rect,
        #dfdContainer [data-semantic-id].hovered-related path,
        #dfdContainer [data-semantic-id].hovered-related ellipse,
        #dfdContainer [data-semantic-id].hovered-related circle { stroke: #1976d2 !important; fill: #e3f2fd !important; cursor: pointer; }

        /* Selected: components — orange, clearly distinct from hover blue */
        #dfdContainer [data-semantic-id]:not([data-element-type="dataflow"]).selected rect,
        #dfdContainer [data-semantic-id]:not([data-element-type="dataflow"]).selected path,
        #dfdContainer [data-semantic-id]:not([data-element-type="dataflow"]).selected ellipse,
        #dfdContainer [data-semantic-id]:not([data-element-type="dataflow"]).selected circle { stroke: #e65100 !important; fill: #ffe0b2 !important; stroke-width: 2px !important; }
        #dfdContainer [data-semantic-id]:not([data-element-type="dataflow"]).selected text { font-weight: bold !important; }

        /* Selected: dataflows — orange stroke, even thicker */
        #dfdContainer [data-element-type="dataflow"].selected path,
        #dfdContainer [data-element-type="dataflow"].selected line,
        #dfdContainer [data-element-type="dataflow"].selected polyline { stroke: #e65100 !important; stroke-width: 5px !important; }

        /* Selected-related: endpoints lit when a dataflow is clicked — orange tint */
        #dfdContainer [data-semantic-id].selected-related rect,
        #dfdContainer [data-semantic-id].selected-related path,
        #dfdContainer [data-semantic-id].selected-related ellipse,
        #dfdContainer [data-semantic-id].selected-related circle { stroke: #e65100 !important; fill: #ffe0b2 !important; stroke-width: 2px !important; }
        #dfdContainer [data-semantic-id].selected-related text { font-weight: bold !important; }

        /* Focus mode: fade everything except the focused element and its related endpoints */
        #dfdContainer.has-focus [data-semantic-id] { transition: opacity 0.25s ease; }
        #dfdContainer.has-focus [data-semantic-id]:not(.focused):not(.focused-related) { opacity: 0.12; }
        #dfdContainer.has-focus [data-semantic-id].focused,
        #dfdContainer.has-focus [data-semantic-id].focused-related { opacity: 1; }

        /* Prevent text selection on double-click */
        #dfdContainer { user-select: none; -webkit-user-select: none; }
      `}</style>

      <DocIntroDialog show={introOpen} onHide={() => setIntroOpen(false)} threatModelProvided={threatModelProvided} />
      <TmViewerTourJoyride key={tourKey} run={joyrideRun} onFinish={() => setJoyrideRun(false)} />

      {/* ── Top bar ── */}
      <div style={{ padding: '8px 16px', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: '#fff' }}>
        <h2 style={{ margin: 0, fontSize: 17 }}>Threat Model Viewer</h2>
        <button onClick={() => setIntroOpen(true)} style={{ ...zoomBtnStyle, fontSize: 11, padding: '4px 10px' }}>📚 Doc Intro</button>
        <button
          onClick={() => { fitToCanvas(); setTourKey(k => k + 1); setJoyrideRun(true); }}
          title="How to use this viewer"
          style={{ ...zoomBtnStyle, fontSize: 11, padding: '4px 10px' }}
        >? How to use</button>
        {!loading && !msg && (
          <div id="tm-zoom-controls" style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
            <button onClick={fitToCanvas} style={{ ...zoomBtnStyle, fontSize: 11, padding: '4px 10px' }}>Fit</button>
            <input
              type="range" min={5} max={300} step={1}
              value={Math.round(zoom * 100)}
              onChange={e => handleSliderChange(parseInt(e.target.value, 10))}
              style={{ width: 110, cursor: 'pointer', accentColor: '#5e35b1' }}
            />
            <span style={{ minWidth: 38, textAlign: 'center', fontSize: 13 }}>{Math.round(zoom * 100)}%</span>
          </div>
        )}
      </div>

      {loading  && <p style={{ padding: 20 }}>Loading Threat Model…</p>}
      {!loading && msg && <p style={{ padding: 20, color: '#b00020' }}>{msg}</p>}

      {/* ── Legend bar ── */}
      {!loading && !msg && (
        <div id="tm-legend-bar" style={{
          padding: '3px 14px', borderBottom: '1px solid #eee',
          background: '#fafafa', display: 'flex', alignItems: 'center',
          flexShrink: 0, fontSize: 12, color: '#666', overflow: 'hidden',
        }}>
          {/* DFD elements */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 11, color: '#aaa', fontWeight: 700, letterSpacing: '0.05em', flexShrink: 0 }}>ELEMENTS</span>
            {[
              [<div style={{ width: 22, height: 13, border: '1.5px solid #555' }} />, 'Ext. Entity'],
              [<div style={{ width: 30, height: 15, border: '1.5px solid #555', borderRadius: '50%' }} />, 'Process'],
              [<svg width="24" height="16" style={{ overflow: 'visible', display: 'block' }}>
                <ellipse cx="12" cy="13" rx="11" ry="3" fill="#fafafa" stroke="#555" strokeWidth="1.5"/>
                <rect x="1" y="4" width="22" height="9" fill="#fafafa" stroke="none"/>
                <line x1="1" y1="4" x2="1" y2="13" stroke="#555" strokeWidth="1.5"/>
                <line x1="23" y1="4" x2="23" y2="13" stroke="#555" strokeWidth="1.5"/>
                <ellipse cx="12" cy="4" rx="11" ry="3" fill="#fafafa" stroke="#555" strokeWidth="1.5"/>
              </svg>, 'Data Store'],
              [<svg width="24" height="10" style={{ overflow: 'visible', display: 'block' }}>
                <defs><marker id="leg-arr" markerWidth="5" markerHeight="5" refX="5" refY="2.5" orient="auto"><path d="M0,0 L5,2.5 L0,5 Z" fill="#555"/></marker></defs>
                <line x1="1" y1="5" x2="19" y2="5" stroke="#555" strokeWidth="1.5" markerEnd="url(#leg-arr)"/>
              </svg>, 'Data Flow'],
              [<div style={{ width: 22, height: 13, border: '1.5px dashed #888' }} />, 'Trust Boundary'],
            ].map(([visual, label]) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
                {visual}
                <span style={{ whiteSpace: 'nowrap' }}>{label}</span>
              </div>
            ))}
          </div>

          <div style={{ width: 1, height: 16, background: '#ddd', margin: '0 10px', flexShrink: 0 }} />

          {/* STRIDE badges */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            <span style={{ fontSize: 11, color: '#aaa', fontWeight: 700, letterSpacing: '0.05em', marginRight: 2 }}>STRIDE</span>
            {Object.entries(STRIDE_COLOR).map(([k, c]) => (
              <span key={k} title={STRIDE_LABEL[k]} style={{
                backgroundColor: c, color: '#fff',
                padding: '0 5px', borderRadius: 5,
                fontSize: 11, fontWeight: 700, cursor: 'default',
              }}>{k}</span>
            ))}
          </div>

          <div style={{ width: 1, height: 16, background: '#ddd', margin: '0 8px', flexShrink: 0 }} />

          {/* Metric icons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, fontSize: 12 }}>
            <span title="Likelihood average">⚡ Likelihood</span>
            <span title="Technical Impact average">⚙ Tech Impact</span>
            <span title="Business Impact average">📈 Biz Impact</span>
            <span title="Risk Score" style={{ fontWeight: 700 }}>⚠ Risk Score</span>
          </div>
        </div>
      )}

      {!loading && !msg && (
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>

          {/* ── DFD canvas ── */}
          <div
            id="dfdViewport"
            style={{ flex: 1, overflow: 'hidden', background: '#fafafa', position: 'relative', cursor: isPanning ? 'grabbing' : 'default' }}
            onContextMenu={e => { if (isDragging.current) e.preventDefault(); }}
            onMouseDown={e => {
              if (e.button !== 1 && e.button !== 2) return;
              e.preventDefault();
              isDragging.current = true;
              dragStart.current = { x: e.clientX, y: e.clientY, panX: liveRef.current.panX, panY: liveRef.current.panY };
              setIsPanning(true);
            }}
            onMouseMove={e => {
              if (!isDragging.current) return;
              applyTransform(liveRef.current.zoom, {
                x: dragStart.current.panX + (e.clientX - dragStart.current.x),
                y: dragStart.current.panY + (e.clientY - dragStart.current.y),
              });
            }}
            onMouseUp={e => { if (e.button === 1 || e.button === 2) { isDragging.current = false; setIsPanning(false); } }}
            onMouseLeave={() => { isDragging.current = false; setIsPanning(false); }}
          >
            <div
              id="dfdContainer"
              dangerouslySetInnerHTML={{ __html: svgContent }}
              style={{ position: 'absolute', transformOrigin: '0 0', transform: `translate(${pan.x}px,${pan.y}px) scale(${zoom})` }}
            />
          </div>

          {/* ── Sidebar collapsed strip ── */}
          {!sidebarOpen && (
            <div
              onClick={() => setSidebarOpen(true)}
              title="Show threats panel"
              onMouseEnter={e => { e.currentTarget.style.background = '#d9cfff'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#ede7f6'; }}
              style={{
                width: 32,
                borderLeft: '3px solid #5e35b1',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                cursor: 'pointer',
                background: '#ede7f6',
                flexShrink: 0,
                userSelect: 'none',
                transition: 'background 0.15s',
              }}
            >
              <span style={{ fontSize: 15, color: '#5e35b1', fontWeight: 700, lineHeight: 1 }}>›</span>
              <span style={{
                writingMode: 'vertical-rl',
                fontSize: 10.5,
                color: '#5e35b1',
                fontWeight: 700,
                letterSpacing: '0.06em',
                transform: 'rotate(180deg)',
              }}>
                {sidebarTab === 'paths' ? 'API PATHS' : 'THREATS'}{sidebarCount > 0 ? ` (${sidebarCount})` : ''}
              </span>
            </div>
          )}

          {/* ── Sidebar resize handle ── */}
          {sidebarOpen && (
            <div
              onMouseDown={e => {
                e.preventDefault();
                sidebarResizing.current      = true;
                sidebarResizeStartX.current  = e.clientX;
                sidebarResizeStartW.current  = sidebarWidth;
                document.body.style.cursor     = 'col-resize';
                document.body.style.userSelect = 'none';
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#c5cae9'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
              style={{
                width: 5,
                flexShrink: 0,
                cursor: 'col-resize',
                background: 'transparent',
                borderLeft: '2px solid #e0e0e0',
                transition: 'background 0.15s',
              }}
            />
          )}

          {/* ── Right sidebar ── */}
          {sidebarOpen && (
            <div id="tm-sidebar" style={{
              width: sidebarWidth,
              display: 'flex',
              flexDirection: 'column',
              background: '#f9f9fb',
              flexShrink: 0,
              overflow: 'hidden',
            }}>

              {/* Sidebar header */}
              <div style={{
                padding: '8px 10px 6px 12px',
                borderBottom: '1px solid #e0e0e0',
                background: '#fff',
                flexShrink: 0,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {selected ? (labelsMap[selected] ?? selected) : 'All Elements'}
                    </div>
                    <div style={{ fontSize: 12, color: '#888', marginTop: 1 }}>
                      {loadingThreats ? 'Loading…' : `${threats.length} threat${threats.length !== 1 ? 's' : ''} · ${apiPaths.length} path${apiPaths.length !== 1 ? 's' : ''}${!selected ? ' (all)' : ''}`}
                    </div>
                  </div>
                  {/* Text scale + Collapse */}
                  <div style={{ display: 'flex', gap: 3, alignItems: 'center', flexShrink: 0, marginLeft: 6 }}>
                    <button onClick={() => adjustTextScale(-0.1)} title="Smaller text" style={scaleBtnStyle}>A−</button>
                    <button onClick={() => adjustTextScale(+0.1)} title="Larger text"  style={scaleBtnStyle}>A+</button>
                    <button
                      onClick={() => setSidebarOpen(false)}
                      title="Hide panel"
                      style={{
                        display: 'flex', alignItems: 'center', gap: 3,
                        padding: '3px 8px', fontSize: 11, fontWeight: 600,
                        border: '1px solid #d0c4f5', borderRadius: 6,
                        background: '#ede7f6', color: '#5e35b1',
                        cursor: 'pointer', marginLeft: 4, whiteSpace: 'nowrap',
                      }}
                    >‹‹ Hide</button>
                  </div>
                </div>

                {/* Tab toggle — Threats / Paths */}
                {(
                  <div style={{ display: 'flex', marginTop: 8, borderRadius: 8, overflow: 'hidden', border: '1px solid #e0e0e0', flexShrink: 0 }}>
                    <button
                      onClick={() => setSidebarTab('threats')}
                      style={{
                        flex: 1, padding: '5px 0', fontSize: 13, fontWeight: sidebarTab === 'threats' ? 700 : 400,
                        background: sidebarTab === 'threats' ? '#5e35b1' : '#fff',
                        color: sidebarTab === 'threats' ? '#fff' : '#666',
                        border: 'none', cursor: 'pointer', transition: 'background 0.15s',
                      }}
                    >
                      ⚠ Threats{!loadingThreats ? ` (${threats.length})` : ''}
                    </button>
                    <button
                      onClick={() => setSidebarTab('paths')}
                      style={{
                        flex: 1, padding: '5px 0', fontSize: 13, fontWeight: sidebarTab === 'paths' ? 700 : 400,
                        background: sidebarTab === 'paths' ? '#1565c0' : '#fff',
                        color: sidebarTab === 'paths' ? '#fff' : '#666',
                        border: 'none', borderLeft: '1px solid #e0e0e0', cursor: 'pointer', transition: 'background 0.15s',
                      }}
                    >
                      🔗 Paths{!loadingThreats ? ` (${apiPaths.length})` : ''}
                    </button>
                  </div>
                )}

                {/* Sort chips — only in threats tab */}
                {sidebarTab === 'threats' && !loadingThreats && threats.length > 0 && (
                  <div id="tm-sort-chips" style={{ display: 'flex', gap: 5, marginTop: 7, flexWrap: 'wrap' }}>
                    {SORT_OPTIONS.map(opt => {
                      const active = sortKey === opt.key;
                      return (
                        <button
                          key={opt.key}
                          onClick={() => handleSort(opt.key)}
                          style={{
                            fontSize: 11.5,
                            padding: '2px 8px',
                            borderRadius: 10,
                            border: `1px solid ${active ? '#5e35b1' : '#ccc'}`,
                            background: active ? '#ede7f6' : '#fff',
                            color: active ? '#5e35b1' : '#555',
                            fontWeight: active ? 700 : 400,
                            cursor: 'pointer',
                            userSelect: 'none',
                          }}
                        >
                          {opt.label} {active ? (sortDir === 'asc' ? '↑' : '↓') : ''}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Cards area */}
              <div style={{ flex: 1, overflow: 'auto', padding: '10px 10px 10px 10px' }}>
                {loadingThreats ? (
                  <p style={{ color: '#888', fontSize: 13 }}>Loading…</p>
                ) : threatsError ? (
                  <p style={{ color: 'red', fontSize: 13 }}>Error: {threatsError}</p>
                ) : sidebarTab === 'paths' ? (
                  apiPaths.length === 0 ? (
                    <p style={{ color: '#aaa', fontSize: 13, textAlign: 'center', marginTop: 40 }}>
                      No API paths defined{selected ? <><br /><strong>{labelsMap[selected] ?? selected}</strong></> : ''}.
                    </p>
                  ) : (
                    apiPaths.map((path, i) => <ApiPathRow key={`${path}-${i}`} path={path} scale={adaptiveScale} />)
                  )
                ) : threats.length === 0 ? (
                  <p style={{ color: '#aaa', fontSize: 13, textAlign: 'center', marginTop: 40 }}>
                    No threats defined{selected ? <><br /><strong>{labelsMap[selected] ?? selected}</strong></> : ''}.
                  </p>
                ) : (
                  threats.map((t, i) => (
                    <ThreatCard
                      key={t.id}
                      id={i === 0 ? 'tm-first-threat-card' : undefined}
                      threat={t}
                      scale={adaptiveScale}
                      isSelected={!selected && selectedThreatId === t.id}
                      onSelect={!selected ? () => {
                        setSelectedThreatId(t.id);
                        highlightCompRef.current?.(t._component);
                        logEvent('click_threat_card', { threat_id: t.id, component: t._component });
                      } : undefined}
                    />
                  ))
                )}
              </div>
            </div>
          )}

          {/* ── BOTTOM PANEL (commented out — kept for reference) ──
          <drag handle />
          <div style={{ height: panelHeight, ... }}>
            panel header, table with COLUMNS
          </div>
          ── end BOTTOM PANEL ── */}

        </div>
      )}

      <DRQuestionPanel category="threat_model" />
    </div>
  );
}

const zoomBtnStyle = {
  padding: '4px 10px', fontSize: 16, fontWeight: 'bold', cursor: 'pointer',
  border: '1px solid #ccc', borderRadius: 4, background: '#f0f0f0',
};

const scaleBtnStyle = {
  padding: '1px 6px', fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
  border: '1px solid #ccc', borderRadius: 4, background: '#f0f0f0',
  userSelect: 'none',
};
